// src/pages/Hmo/FeedbackForm.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { toast } from 'react-toastify';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import TopHeader from '../../components/dashboard/TopHeader/TopHeader';
import TestModeBanner from '../../components/dashboard/TestModeBanner';
import Sidebar from '../../components/Global/sidebar'; // ✅ Import Reusable Sidebar
import { authenticatedFetch, clearStoredCredentials } from '../../utils/api';

// 🎨 Orange Theme Colors
const BRAND_ORANGE = '#F89423';
const BRAND_ORANGE_DARK = '#ea7c1f';

function FeedbackForm() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Simplified Form State
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    companyName: '',
    phoneNumber: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize AOS
  useEffect(() => {
    AOS.init({ duration: 800, once: true, easing: 'ease-out-cubic' });
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { 
      toast.error('Please fill in all required fields'); 
      return; 
    }

    setIsSubmitting(true);

    try {
      const response = await authenticatedFetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          timestamp: new Date().toISOString()
        })
      });

      if (response?.success || response?.ok) {
        toast.success('Thank you! Your message has been sent successfully.');
        setFormData({
          fullName: '',
          email: '',
          companyName: '',
          phoneNumber: '',
          message: ''
        });
      } else {
        throw new Error(response?.message || 'Submission failed');
      }
    } catch (error) {
      console.error('Feedback error:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ✅ Navigation & Logout Handlers
  const handleNavClick = (path, e) => {
    e?.preventDefault();
    if (path) navigate(path);
    if (window.innerWidth < 992) setSidebarOpen(false);
  };

  const handleLogout = (e) => {
    e?.preventDefault();
    clearStoredCredentials();
    localStorage.removeItem('userSession');
    toast.success('Logged out successfully');
    navigate('/login', { replace: true });
  };

  return (
    <div className="business-dashboard-page">
      
      {/* ✅ Left Sidebar (Using Reusable Component) */}
      <Sidebar 
        sidebarOpen={sidebarOpen} 
        onNavClick={handleNavClick} 
        onLogout={handleLogout} 
      />

      {/* Main Content */}
      <div className="main-content">
        <TopHeader 
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          pageTitle="Feedback"
          pageIcon="fa-comments"
        />
        

        <div className="content-wrapper" style={{ padding: '40px 30px' }}>
          
          {/* Page Header */}
          <div className="mb-5" data-aos="fade-down">
            <h1 className="page-title" style={{ 
              fontSize: '2.5rem', 
              fontWeight: '600', 
              color: '#1f2937',
              marginBottom: '10px'
            }}>
              Get in Touch
            </h1>
            <p className="text-muted" style={{ fontSize: '1.1rem' }}>
              We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </p>
          </div>

          {/* Clean Feedback Form Card */}
          <Card className="contact-form-card border-0 shadow-sm" data-aos="fade-up">
            <form onSubmit={handleSubmit} className="p-5">
              
              {/* Name & Email Row */}
              <div className="row g-4 mb-4">
                <div className="col-md-6" data-aos="fade-up" data-aos-delay="50">
                  <InputText
                    value={formData.fullName}
                    onChange={(e) => handleChange('fullName', e.target.value)}
                    placeholder="Full Name"
                    className={`w-100 form-input ${errors.fullName ? 'input-error' : ''}`}
                    disabled={isSubmitting}
                  />
                  {errors.fullName && <small className="text-danger mt-1 d-block">{errors.fullName}</small>}
                </div>
                <div className="col-md-6" data-aos="fade-up" data-aos-delay="100">
                  <InputText
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="Email Address"
                    className={`w-100 form-input ${errors.email ? 'input-error' : ''}`}
                    disabled={isSubmitting}
                  />
                  {errors.email && <small className="text-danger mt-1 d-block">{errors.email}</small>}
                </div>
              </div>

              {/* Company & Phone Row */}
              <div className="row g-4 mb-4">
                <div className="col-md-6" data-aos="fade-up" data-aos-delay="150">
                  <InputText
                    value={formData.companyName}
                    onChange={(e) => handleChange('companyName', e.target.value)}
                    placeholder="Company Name"
                    className="w-100 form-input"
                    disabled={isSubmitting}
                  />
                </div>
                <div className="col-md-6" data-aos="fade-up" data-aos-delay="200">
                  <InputText
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => handleChange('phoneNumber', e.target.value)}
                    placeholder="Phone Number"
                    className="w-100 form-input"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              {/* Message Field */}
              <div className="mb-4" data-aos="fade-up" data-aos-delay="250">
                <InputTextarea
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  placeholder="Message"
                  rows={6}
                  className={`w-100 form-textarea ${errors.message ? 'input-error' : ''}`}
                  disabled={isSubmitting}
                  style={{ resize: 'vertical' }}
                />
                {errors.message && <small className="text-danger mt-1 d-block">{errors.message}</small>}
              </div>

              {/* Submit Button */}
              <div className="mt-5" data-aos="fade-up" data-aos-delay="300">
                <Button
                  type="submit"
                  label={isSubmitting ? 'Sending...' : 'Send Message'}
                  icon={isSubmitting ? 'pi pi-spin pi-spinner' : ''}
                  disabled={isSubmitting}
                  className="w-100 submit-button"
                  style={{
                    background: BRAND_ORANGE,
                    borderColor: BRAND_ORANGE,
                    height: '54px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    borderRadius: '8px',
                    boxShadow: '0 4px 14px rgba(248, 148, 35, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                />
              </div>
            </form>
          </Card>

        </div>
      </div>

      {/* 🎨 Clean Minimal Styles (Sidebar styles removed since they are in Sidebar.css now) */}
      <style>{`
        .main-content { flex: 1; margin-left: 260px; display: flex; flex-direction: column; background: #f9fafb; min-height: 100vh; }
        
        /* Clean Form Styles */
        .contact-form-card {
          border-radius: 16px !important;
          background: white !important;
          max-width: 900px;
          margin: 0 auto;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important;
        }
        
        .form-input,
        .form-textarea {
          border: 1px solid #e5e7eb !important;
          border-radius: 8px !important;
          padding: 16px 20px !important;
          font-size: 1rem !important;
          transition: all 0.3s ease !important;
          background: #fafafa !important;
          font-family: inherit !important;
        }
        
        .form-input:hover,
        .form-textarea:hover {
          border-color: #d1d5db !important;
          background: white !important;
        }
        
        .form-input:focus,
        .form-textarea:focus {
          border-color: ${BRAND_ORANGE} !important;
          background: white !important;
          box-shadow: 0 0 0 4px rgba(248, 148, 35, 0.1) !important;
          outline: none !important;
        }
        
        .form-input.input-error,
        .form-textarea.input-error {
          border-color: #ef4444 !important;
          background: #fef2f2 !important;
        }
        
        .form-textarea {
          min-height: 150px !important;
        }
        
        .submit-button {
          background: ${BRAND_ORANGE} !important;
          border: none !important;
          color: white !important;
          transition: all 0.3s ease !important;
        }
        
        .submit-button:hover:not(:disabled) {
          background: ${BRAND_ORANGE_DARK} !important;
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(248, 148, 35, 0.4) !important;
        }
        
        .submit-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        
        /* Responsive */
        @media (max-width: 992px) {
          .main-content { margin-left: 0; }
          .content-wrapper { padding: 20px 15px !important; }
          .contact-form-card .p-5 { padding: 1.5rem !important; }
          .page-title { font-size: 2rem !important; }
        }
        
        /* AOS Animation */
        [data-aos] {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }
        [data-aos].aos-animate {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </div>
  );
}

export default FeedbackForm;